import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, snakeCase } from 'lodash-es';
import { useInView } from 'react-intersection-observer';

import { SET_AUTOPLAY_VIDEO_IN_VIEW } from '../../shared/entity-actions';
import { useFeedEntityContext } from '../../shared/FeedEntityContext';
import { AUTOPLAY_MARGIN_TOP } from '../../../constants';

import PhotosAndMapImage from '../../shared/PhotosAndMapImage';
import HeaderWithOwnerMetadata from '../../shared/HeaderWithOwnerMetadata/HeaderWithOwnerMetadata';
import ActivityEntryBody from '../../shared/ActivityEntryBody';
import MaharajAchievementCelebration from '../../shared/MaharajAchievementCelebration';
import KudosAndComments from '../../shared/KudosAndComments';

import EntryBody from '../../shared/media/EntryBody';
import EntryMedia from '../../shared/media/EntryMedia';
import EntryFooter from '../../shared/media/EntryFooter';

import {
  viewingAthleteProps,
  photoListProps,
  statsProps,
  segAndBestEffortAchievementsProps,
  powerAndSegmentGoalAchievementsProps,
  cumulativeGoalAchievementProps
} from '../../../props/feed-prop-types';

import styles from './GroupActivityEntry.scss';

const GroupActivityEntry = ({
  entity,
  page,
  viewingAthlete,
  photos,
  activity,
  kudosAndComments,
  embedDropdownEnabled
}) => {
  const entityOwner = {
    displayImage: activity.athlete_avatar_url,
    name: activity.athlete_name,
    memberType: activity.member_type,
    profileUrl: `/athletes/${activity.athlete_id}`,
    type: 'athlete',
    id: activity.athlete_id.toString()
  };

  const noAchievementsAndMapsPhotos =
    isEmpty(activity.web_achievement) &&
    (photos && isEmpty(photos[activity.entity_id]));

  const {
    feedEntityState: { autoPlayVideos, isAutoplayEnabled },
    feedDispatch
  } = useFeedEntityContext();

  //
  // Handle autoplay when activity enters area we want to trigger autoplay
  //
  const pageHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const [inViewAutoPlayRef, inViewAutoPlay] = useInView({
    threshold: 0,
    rootMargin: `-${AUTOPLAY_MARGIN_TOP}px 0px -${pageHeight -
      AUTOPLAY_MARGIN_TOP}px 0px`
  });

  const autoPlayVideo = autoPlayVideos[activity.entity_id.toString()];
  useEffect(() => {
    if (isAutoplayEnabled && autoPlayVideo) {
      if (inViewAutoPlay) {
        autoPlayVideo.play();
        feedDispatch({
          type: SET_AUTOPLAY_VIDEO_IN_VIEW,
          payload: { autoPlayVideoInView: true }
        });
      } else {
        autoPlayVideo.pause();
        feedDispatch({
          type: SET_AUTOPLAY_VIDEO_IN_VIEW,
          payload: { autoPlayVideoInView: false }
        });
      }
    }
  }, [isAutoplayEnabled, autoPlayVideo, inViewAutoPlay, feedDispatch]);

  return (
    <li ref={inViewAutoPlayRef}>
      {/* Entry Header - avatar, social actions */}
      <HeaderWithOwnerMetadata
        entityOwner={entityOwner}
        entityId={activity.entity_id.toString()}
        isBoosted={activity.is_boosted || false}
        shareable={activity.shareable || false}
        twitterUrl={activity.twitter_url}
        entity={entity}
        embedDropdownEnabled={embedDropdownEnabled}
      />

      {/* Entry Body =  activity title, description, stats and achievements  */}
      <EntryBody inset={true} className={styles.entryBody}>
        <ActivityEntryBody
          stats={activity.stats}
          description={activity.description}
          flagged={activity.flagged}
          activityName={activity.name}
          activityId={activity.entity_id.toString()}
          ownedByCurrentAthlete={activity.owned_by_current_athlete}
          segAndBestEffortAchievements={activity.achievements}
          powerAndSegmentGoalAchievements={activity.performance_goals}
          cumulativeGoalAchievement={activity.goals}
          sportType={activity.type}
          athleteFirstName={activity.athlete_firstname}
        />
      </EntryBody>

      {/* Maharaj achievements */}
      {!isEmpty(activity.web_achievement) && (
        <EntryMedia inset={true}>
          <MaharajAchievementCelebration
            image={activity.web_achievement.image}
            image_caption={activity.web_achievement.image_caption}
            subtitle={activity.web_achievement.subtitle}
            text={activity.web_achievement.text}
            url={activity.web_achievement.url}
          />
        </EntryMedia>
      )}

      {/* Map and Photos */}
      {photos && !isEmpty(photos[activity.entity_id]) && (
        <EntryMedia inset={true}>
          <PhotosAndMapImage
            activityId={activity.entity_id.toString()}
            entryType={snakeCase(entity)}
            photoList={photos[activity.entity_id].photoList}
            feedName={page}
          />
        </EntryMedia>
      )}

      {/* Kudos And Comments */}
      <EntryFooter
        inset={true}
        className={`${
          noAchievementsAndMapsPhotos ? styles.noAchievementsAndMapsPhotos : ''
        }`}
      >
        <KudosAndComments
          kudosAndComments={kudosAndComments}
          entity={snakeCase(entity)}
          entityId={activity.entity_id.toString()}
          ownedByCurrentAthlete={activity.owned_by_current_athlete}
          viewingAthlete={viewingAthlete}
          entityOwner={entityOwner}
          entityTitle={activity.name}
          openCommentsModalOnIconClick={true}
          segAndBestEffortAchievements={activity.achievements}
          showHairlineOnTop={false}
        />
      </EntryFooter>
    </li>
  );
};

GroupActivityEntry.propTypes = {
  entity: PropTypes.string.isRequired,
  page: PropTypes.string.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  photos: PropTypes.shape({
    photoList: photoListProps
  }).isRequired,
  activity: PropTypes.shape({
    athlete_id: PropTypes.number,
    athlete_avatar_url: PropTypes.string,
    athlete_name: PropTypes.string,
    athlete_firstname: PropTypes.string,
    member_type: PropTypes.string,
    name: PropTypes.string,
    type: PropTypes.string,
    description: PropTypes.string,
    activity_class_name: PropTypes.string,
    flagged: PropTypes.bool,
    entity_id: PropTypes.number,
    is_routable: PropTypes.bool,
    is_commute: PropTypes.bool,
    is_virtual: PropTypes.bool,
    workout_type: PropTypes.string,
    privacy_tag_key: PropTypes.string,
    stats: statsProps,
    owned_by_current_athlete: PropTypes.bool,
    achievements: segAndBestEffortAchievementsProps,
    performance_goals: powerAndSegmentGoalAchievementsProps,
    goals: cumulativeGoalAchievementProps,
    web_achievement: PropTypes.shape({
      image: PropTypes.string,
      image_caption: PropTypes.string,
      subtitle: PropTypes.string,
      text: PropTypes.string,
      url: PropTypes.string
    }),
    is_boosted: PropTypes.bool,
    shareable: PropTypes.bool,
    twitter_url: PropTypes.string,
    photos: PropTypes.arrayOf(
      PropTypes.shape({
        urls: PropTypes.shape({}).isRequired,
        caption: PropTypes.string
      })
    )
  }).isRequired,
  kudosAndComments: PropTypes.shape({}).isRequired,
  embedDropdownEnabled: PropTypes.bool
};

GroupActivityEntry.defaultProps = {
  embedDropdownEnabled: false
};

export default GroupActivityEntry;
